/**
 * Options API mixin: emits modal_open / modal_close Faro events for a modal.
 * `openKey` is the prop or data key holding the open state (isModalOpen for
 * v-model modals, isOpen for ConfirmModal). Many modals are mounted with v-if
 * only once opened, so the state at mount and unmount counts too, not only
 * the flips seen by the watcher.
 */
import { resolveModalName } from './faroContext'
import { trackModalClose, trackModalOpen } from './faro'

type ModalTrackers = {
  trackModalOpen(modal: string, route: string): void
  trackModalClose(modal: string, route: string, durationMs: number): void
}

/** The modal instance the mixin runs on. */
type TrackedModal = {
  trackingName?: string
  trackingOpenedAt: number | null
  $?: {
    vnode?: { ctx?: { type?: Parameters<typeof resolveModalName>[1] } | null }
  }
  $parent?: { $options?: Parameters<typeof resolveModalName>[1] } | null
  $route?: { name?: unknown }
  [openKey: string]: unknown
}

export function modalTracking(
  openKey: string,
  trackers: ModalTrackers = { trackModalOpen, trackModalClose }
) {
  function context(component: TrackedModal) {
    return {
      // Template owner, like buttons: a modal slotted into a wrapper (card,
      // layout) belongs to the component that wrote it, not to the wrapper.
      modal: resolveModalName(
        component.trackingName,
        component.$?.vnode?.ctx?.type || component.$parent?.$options
      ),
      route:
        typeof component.$route?.name === 'string'
          ? component.$route.name
          : 'unknown'
    }
  }

  function opened(component: TrackedModal) {
    const { modal, route } = context(component)
    component.trackingOpenedAt = performance.now()
    trackers.trackModalOpen(modal, route)
  }

  function closed(component: TrackedModal) {
    if (component.trackingOpenedAt === null) {
      return
    }
    const { modal, route } = context(component)
    trackers.trackModalClose(
      modal,
      route,
      performance.now() - component.trackingOpenedAt
    )
    component.trackingOpenedAt = null
  }

  return {
    props: {
      trackingName: { type: String, default: undefined }
    },
    data() {
      return { trackingOpenedAt: null as number | null }
    },
    mounted(this: TrackedModal) {
      if (this[openKey]) {
        opened(this)
      }
    },
    beforeUnmount(this: TrackedModal) {
      closed(this)
    },
    watch: {
      [openKey](this: TrackedModal, isOpen: boolean) {
        if (isOpen) {
          opened(this)
          return
        }
        closed(this)
      }
    }
  }
}
